import { ContactInfo, JobOfferEmbedDetailsShowingV2ResponseDto } from "@/lib/interfaces/embed";
import { Spinner } from "@chakra-ui/react";
import type { IframeSettingsDesignDraft, IframeSettingsTextDraft } from "./IframeSettingsPreviewContext";
import { JobPostsV2AboutUs } from "./job-posts-v2/JobPostsV2AboutUs";
import { JobPostsV2ApplicationForm } from "./job-posts-v2/JobPostsV2ApplicationForm";
import { JobPostsV2Benefits } from "./job-posts-v2/JobPostsV2Benefits";
import { JobPostsV2Header } from "./job-posts-v2/JobPostsV2Header";
import { JobPostsV2Hero } from "./job-posts-v2/JobPostsV2Hero";
import { JobPostsV2Process } from "./job-posts-v2/JobPostsV2Process";
import { JobPostsV2Requirements } from "./job-posts-v2/JobPostsV2Requirements";
import { JobPostsV2Responsibilities } from "./job-posts-v2/JobPostsV2Responsibilities";
import { JobPostsV2SimilarJobs } from "./job-posts-v2/JobPostsV2SimilarJobs";
import { useEmbedJobStyles } from "./job-posts-v2/use-embed-job-styles";
import { useJobPostsV2Display } from "./job-posts-v2/use-job-posts-v2-display";
import { useJobPostsV2DebugEnabled, useJobPostsV2Source } from "./job-posts-v2/use-job-posts-v2-source";
import { withAlpha } from "./job-posts-v2/v2-helpers";

export type JobPostsV2SettingsPreview = {
  showMockBanner?: boolean;
  disableSimilarJobNavigation?: boolean;
  /** When set (e.g. Karriereportal settings preview), shows a contact block from embed contact copy. */
  contactSection?: ContactInfo;
};

export type JobPostsV2Props = {
  organizationId: string;
  id: string;
  /** Raw job detail for Karriereportal live preview; merged in-component with `previewDesign` / `previewTextDraft`. */
  previewRawDetail?: JobOfferEmbedDetailsShowingV2ResponseDto | null;
  previewDesign?: IframeSettingsDesignDraft;
  previewTextDraft?: IframeSettingsTextDraft;
  readOnlyApplication?: boolean;
  settingsPreview?: JobPostsV2SettingsPreview;
};

const JobPostsV2 = ({
  organizationId,
  id,
  previewRawDetail,
  previewDesign,
  previewTextDraft,
  readOnlyApplication,
  settingsPreview,
}: JobPostsV2Props) => {
  const debug = useJobPostsV2DebugEnabled();

  const { isLiveSettingsPreview, sourceJobPost, isLoading, embedBaseSettings } = useJobPostsV2Source({
    organizationId,
    id,
    previewRawDetail,
    previewDesign,
    previewTextDraft,
    debug,
  });

  const { mergedVisual } = useEmbedJobStyles({
    organizationId,
    jobId: id,
    isLiveSettingsPreview,
    previewDesign,
    embedBaseSettings,
    sourceJobPost,
    settingsPreview,
  });

  const displayJobPost = useJobPostsV2Display({
    sourceJobPost,
    embedBaseSettings,
    isLiveSettingsPreview,
    mergedColor: mergedVisual.color,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center w-full min-h-screen items-center bg-gray-50">
        <Spinner size="xl" color={sourceJobPost?.organization.color || "#3182ce"} />
      </div>
    );
  }

  if (!sourceJobPost || !displayJobPost) {
    return (
      <div className="flex justify-center w-full min-h-screen items-center bg-gray-50">
        <p>Keine Stellenanzeige gefunden</p>
      </div>
    );
  }

  const brandColor = displayJobPost.organization.color || "#3182ce";

  const embedBodySections = [
    displayJobPost.benefits
      ? {
          key: "benefits",
          render: () => <JobPostsV2Benefits brandColor={brandColor} benefits={displayJobPost.benefits} />,
        }
      : null,
    displayJobPost.aboutUs
      ? {
          key: "about-us",
          render: () => (
            <JobPostsV2AboutUs organizationName={displayJobPost.organization.name} aboutUs={displayJobPost.aboutUs} />
          ),
        }
      : null,
    displayJobPost.responsibilities
      ? {
          key: "responsibilities",
          render: () => (
            <JobPostsV2Responsibilities brandColor={brandColor} responsibilities={displayJobPost.responsibilities} />
          ),
        }
      : null,
    displayJobPost.requirements
      ? {
          key: "requirements",
          render: () => <JobPostsV2Requirements brandColor={brandColor} requirements={displayJobPost.requirements} />,
        }
      : null,
    displayJobPost.process
      ? {
          key: "process",
          render: () => <JobPostsV2Process brandColor={brandColor} process={displayJobPost.process} />,
        }
      : null,
  ].filter(Boolean) as Array<{ key: string; render: () => JSX.Element }>;

  const contact = settingsPreview?.contactSection;
  const showContactSection = contact && (contact.title?.trim().length > 0 || contact.description?.trim().length > 0);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="embed-job-root min-h-screen bg-white">
      {settingsPreview?.showMockBanner ? (
        <div
          className="embed-job-demo-banner w-full px-4 py-3 text-center text-sm font-medium text-amber-900"
          style={{ backgroundColor: withAlpha(brandColor, 0.15) }}
        >
          Diese Demo-Vorschau dient lediglich zur Ansicht. Es werden Beispieldaten angezeigt, wie die
          Karriereportal-Stelle aussehen kann
        </div>
      ) : null}
      <JobPostsV2Header
        brandColor={brandColor}
        organizationName={displayJobPost.organization.name}
        logoUrl={displayJobPost.organization.logoUrl}
        onApplyClick={() => scrollToSection("application-form")}
      />
      <JobPostsV2Hero
        heroEyebrowText={displayJobPost.embedSettings?.heroEyebrowText}
        brandColor={brandColor}
        jobOfferPreview={displayJobPost.jobOfferPreview}
        onApplyClick={() => scrollToSection("application-form")}
      />
      {embedBodySections.map((section, idx) => {
        const rendered = section.render();
        if (!rendered) return null;
        return (
          <section
            key={section.key}
            className={`embed-job-body-section w-full ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
          >
            <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">{rendered}</div>
          </section>
        );
      })}
      <JobPostsV2SimilarJobs
        sectionClassName={`w-full ${embedBodySections.length % 2 === 0 ? "bg-white" : "bg-gray-50"}`}
        organizationId={organizationId}
        currentJobId={id}
        brandColor={brandColor}
        initialSimilarJobs={displayJobPost.similarJobs}
        isDebug={debug}
        disableNavigation={settingsPreview?.disableSimilarJobNavigation}
      />
      {showContactSection ? (
        <section
          className={`embed-job-contact w-full ${embedBodySections.length % 2 === 0 ? "bg-gray-50" : "bg-white"}`}
        >
          <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
            <div className="mx-auto max-w-2xl text-center">
              {contact!.title?.trim() ? (
                <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900">{contact!.title}</h2>
              ) : null}
              {contact!.description?.trim() ? (
                <p className="mt-3 text-sm md:text-base text-gray-600 whitespace-pre-wrap">{contact!.description}</p>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}
      <section
        key="form"
        className="embed-job-application w-full"
        style={{ backgroundColor: withAlpha(brandColor, 0.1) }}
      >
        <div className="mx-auto max-w-6xl px-4 py-12 md:py-16">
          <JobPostsV2ApplicationForm
            jobOfferId={displayJobPost.jobOfferPreview._id || ""}
            color={brandColor}
            readOnly={readOnlyApplication}
          />
        </div>
      </section>

      <footer className="embed-job-footer w-full bg-white border-t border-gray-200">
        <div className="mx-auto max-w-6xl px-4 py-8 text-center text-xs text-gray-500">
          <div>
            © 2026 {displayJobPost.organization.name}
            {displayJobPost.jobOfferPreview.location ? ` – ${displayJobPost.jobOfferPreview.location}` : ""}
          </div>
          <div className="mt-2 flex items-center justify-center gap-2">
            {displayJobPost.organization.imprintLink ? (
              <a
                href={displayJobPost.organization.imprintLink}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                Impressum
              </a>
            ) : null}
            {displayJobPost.organization.imprintLink && displayJobPost.organization.privacyPolicyLink ? (
              <span>|</span>
            ) : null}
            {displayJobPost.organization.privacyPolicyLink ? (
              <a
                href={displayJobPost.organization.privacyPolicyLink}
                target="_blank"
                rel="noreferrer"
                className="hover:underline"
              >
                Datenschutz
              </a>
            ) : null}
          </div>
        </div>
      </footer>
    </div>
  );
};

export { JobPostsV2 };
